import { useEffect, useState } from 'react';
import { Alert, Box, Dialog, DialogContent, DialogTitle, Grid, IconButton, LinearProgress, Paper, Snackbar, SnackbarCloseReason, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import * as yup from 'yup';
import { VTextField, VForm, useVForm, IVFormErrors, BasicSelect, AutoCompleteParceiro } from '../../../shared/forms';
import { FerramentasDeDetalhe, TelaBloqueioSalvando } from '../../../shared/components';
import { useBloqueioSalvamento } from '../../../shared/hooks';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { BensService, IBemParceiroHistorico, TTipoBem } from '../../../shared/services/api/servicos/BensService';

interface IFormData {
  // Opcional: bem pode ser cadastrado sem cliente vinculado ainda (ex.:
  // atendimento em que o cliente não está cadastrado no sistema no momento)
  // e o vínculo é feito depois editando o bem.
  idparceiro?: string;
  tipo: TTipoBem;
  descricao: string;
  marca?: string;
  modelo?: string;
  ano?: string;
  cor?: string;
  placa?: string;
  chassi?: string;
  renavam?: string;
  quilometragem?: number;
  combustivel?: string;
  horimetro?: number;
  numeroserie?: string;
  patrimonio?: string;
  observacao?: string;
}

const formValidationSchema: yup.ObjectSchema<IFormData> = yup.object().shape({
  idparceiro: yup.string().optional(),
  tipo: yup.mixed<TTipoBem>().oneOf(['VEICULO', 'MOTO', 'MAQUINA', 'EQUIPAMENTO', 'PATRIMONIO', 'OUTRO']).required('Tipo é obrigatório'),
  descricao: yup.string().required('Descrição é obrigatória').min(2).max(255),
  marca: yup.string().optional().default(''),
  modelo: yup.string().optional().default(''),
  ano: yup.string().optional().default(''),
  cor: yup.string().optional().default(''),
  placa: yup.string().optional().default(''),
  chassi: yup.string().optional().default(''),
  renavam: yup.string().optional().default(''),
  quilometragem: yup.number().optional(),
  combustivel: yup.string().optional().default(''),
  horimetro: yup.number().optional(),
  numeroserie: yup.string().optional().default(''),
  patrimonio: yup.string().optional().default(''),
  observacao: yup.string().optional().default(''),
});

const TIPOS_BEM = [
  { nameBD: 'VEICULO', descricao: 'Veículo' },
  { nameBD: 'MOTO', descricao: 'Moto' },
  { nameBD: 'MAQUINA', descricao: 'Máquina' },
  { nameBD: 'EQUIPAMENTO', descricao: 'Equipamento' },
  { nameBD: 'PATRIMONIO', descricao: 'Patrimônio' },
  { nameBD: 'OUTRO', descricao: 'Outro' },
];

export const DetalheDeBem: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const navigate = useNavigate();
  const { formRef, save, saveAndClose } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const { isSaving, progress: saveProgress, iniciar: iniciarSalvamento, concluir: concluirSalvamento, cancelar: cancelarSalvamento } = useBloqueioSalvamento();
  const [tipoSelecionado, setTipoSelecionado] = useState<TTipoBem | ''>('');
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const [openHistoricoProprietarios, setOpenHistoricoProprietarios] = useState(false);
  const [carregandoHistoricoProprietarios, setCarregandoHistoricoProprietarios] = useState(false);
  const [historicoProprietarios, setHistoricoProprietarios] = useState<IBemParceiroHistorico[]>([]);

  const ehVeiculo = tipoSelecionado === 'VEICULO' || tipoSelecionado === 'MOTO';
  const ehMaquina = tipoSelecionado === 'MAQUINA' || tipoSelecionado === 'EQUIPAMENTO';
  const ehPatrimonio = tipoSelecionado === 'PATRIMONIO';

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    if (tms === 'success') {
      navigate('/bens');
    }
  };

  const handleAbrirHistoricoProprietarios = () => {
    if (id === 'novo') return;
    setOpenHistoricoProprietarios(true);
    setCarregandoHistoricoProprietarios(true);
    BensService.getHistoricoParceiro(id).then((result) => {
      setCarregandoHistoricoProprietarios(false);
      if (!(result instanceof Error)) setHistoricoProprietarios(result);
    });
  };

  useEffect(() => {
    if (id !== 'novo') {
      setIsLoading(true);
      BensService.getById(id)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setTipoSelecionado(result.tipo);
            formRef.current?.setData(result);
          }
        });
    } else {
      formRef.current?.setData({
        idparceiro: '',
        tipo: '',
        descricao: '',
      });
    }
  }, [id]);

  const handleSave = (dados: IFormData) => {
    if (!iniciarSalvamento()) return;

    formValidationSchema
      .validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);
        if (id === 'novo') {
          BensService
            .create({ ...dadosValidados, ativo: true })
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                cancelarSalvamento();
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Bem cadastrado com sucesso!');
                setOpenmsg(true);
                concluirSalvamento();
                navigate(`/bens/${result}`);
              }
            });
        } else {
          BensService
            .updateById(id, { ...dadosValidados, ativo: true })
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                cancelarSalvamento();
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Alteração concluída com sucesso!');
                setOpenmsg(true);
                concluirSalvamento();
              }
            });
        }
      })
      .catch((errors: yup.ValidationError) => {
        cancelarSalvamento();
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  return (
    <>
      <TelaBloqueioSalvando
        open={isSaving}
        message={id === 'novo' ? 'Salvando bem...' : 'Salvando alterações...'}
        progress={saveProgress}
      />
      <LayoutBaseDePagina
        titulo={id === 'novo' ? 'Novo Bem' : 'Editar Bem'}
        barraDeFerramentas={
          <FerramentasDeDetalhe
            textoBotaoNovo='Novo'
            mostrarBotaoSalvarEFechar
            mostrarBotaoSalvarCarregando={isLoading}
            mostrarBotaoSalvarEFecharCarregando={isLoading}
            mostrarBotaoNovo={id !== 'novo'}
            mostrarBotaoApagar={false}
            mostrarBotaoImpressao={false}
            disableBotaoSalvar={isLoading || isSaving}
            aoClicarEmSalvar={save}
            aoClicarEmSalvarEFechar={saveAndClose}
            aoClicarEmVoltar={() => navigate('/bens')}
            mostrarBotaoVoltarCarregando={isLoading}
            aoClicarEmNovo={() => navigate('/bens/novo')}
            mostrarBotaoNovoCarregando={isLoading}
          />
        }
      >
        <VForm ref={formRef} onSubmit={handleSave} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 900} onClose={handleCloseMsg}>
            <Alert
              onClose={handleCloseMsg}
              severity={tms}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {msg}
            </Alert>
          </Snackbar>
          <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
            <Grid container direction="column" padding={2} spacing={2}>
              {isLoading && (
                <Grid item>
                  <LinearProgress variant='indeterminate' />
                </Grid>
              )}
              <Grid container item direction="row" spacing={2} alignItems='center'>
                <Grid item xs={id === 'novo' ? 12 : 11} sm={id === 'novo' ? 12 : 11} md={id === 'novo' ? 7 : 6} lg={id === 'novo' ? 6 : 5} xl={id === 'novo' ? 5 : 4}>
                  <AutoCompleteParceiro isExternalLoading={isLoading} />
                </Grid>
                {id !== 'novo' && (
                  <Grid item xs={1}>
                    <Tooltip title='Histórico de proprietários'>
                      <IconButton onClick={handleAbrirHistoricoProprietarios}>
                        <HistoryIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={3} lg={3} xl={2}>
                  <BasicSelect
                    name='tipo'
                    label='Tipo'
                    data={TIPOS_BEM}
                    aoClicar={(valor) => setTipoSelecionado((valor as TTipoBem) ?? '')}
                    disabled={isLoading}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <VTextField
                    size='small'
                    fullWidth
                    name='descricao'
                    disabled={isLoading}
                    label='Descrição'
                    placeholder='Ex.: Gol 1.6 branco, Empilhadeira Hyster, Notebook Dell'
                  />
                </Grid>

                {(ehVeiculo || ehMaquina) && (
                  <>
                    <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
                      <VTextField size='small' fullWidth name='marca' disabled={isLoading} label='Marca' />
                    </Grid>
                    <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
                      <VTextField size='small' fullWidth name='modelo' disabled={isLoading} label='Modelo' />
                    </Grid>
                    <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
                      <VTextField size='small' fullWidth name='ano' disabled={isLoading} label='Ano' />
                    </Grid>
                  </>
                )}

                {ehVeiculo && (
                  <>
                    <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
                      <VTextField size='small' fullWidth name='cor' disabled={isLoading} label='Cor' />
                    </Grid>
                    <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
                      <VTextField size='small' fullWidth name='placa' disabled={isLoading} label='Placa' />
                    </Grid>
                    <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
                      <VTextField size='small' fullWidth name='chassi' disabled={isLoading} label='Chassi' />
                    </Grid>
                    <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
                      <VTextField size='small' fullWidth name='renavam' disabled={isLoading} label='Renavam' />
                    </Grid>
                    <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
                      <VTextField size='small' fullWidth name='quilometragem' disabled={isLoading} label='Quilometragem' />
                    </Grid>
                    <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
                      <VTextField size='small' fullWidth name='combustivel' disabled={isLoading} label='Combustível' />
                    </Grid>
                  </>
                )}

                {ehMaquina && (
                  <>
                    <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
                      <VTextField size='small' fullWidth name='horimetro' disabled={isLoading} label='Horímetro' />
                    </Grid>
                    <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
                      <VTextField size='small' fullWidth name='numeroserie' disabled={isLoading} label='Número de Série' />
                    </Grid>
                    <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
                      <VTextField size='small' fullWidth name='patrimonio' disabled={isLoading} label='Patrimônio' />
                    </Grid>
                  </>
                )}

                {ehPatrimonio && (
                  <>
                    <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
                      <VTextField size='small' fullWidth name='patrimonio' disabled={isLoading} label='Patrimônio' />
                    </Grid>
                    <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
                      <VTextField size='small' fullWidth name='numeroserie' disabled={isLoading} label='Número de Série' />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <VTextField size='small' fullWidth multiline rows={2} name='observacao' disabled={isLoading} label='Observação' />
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </VForm>
      </LayoutBaseDePagina>

      <Dialog open={openHistoricoProprietarios} onClose={() => setOpenHistoricoProprietarios(false)} maxWidth='xs' fullWidth>
        <DialogTitle>Histórico de Proprietários</DialogTitle>
        <DialogContent>
          {carregandoHistoricoProprietarios && <LinearProgress variant='indeterminate' sx={{ mb: 2 }} />}
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Cliente</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historicoProprietarios.map((item) => (
                <TableRow key={item.idhistorico}>
                  <TableCell>{dayjs(item.criadoem).format('DD/MM/YYYY HH:mm')}</TableCell>
                  <TableCell>{item.nomeparceiro || <Typography variant='body2' color='text.secondary'>Sem cliente vinculado</Typography>}</TableCell>
                </TableRow>
              ))}
              {historicoProprietarios.length === 0 && !carregandoHistoricoProprietarios && (
                <TableRow>
                  <TableCell colSpan={2}>Nenhum vínculo registrado ainda.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </>
  );
};
